import { z } from "zod";
import { campaignActionSchema } from "../../../skills/leadcue-reply/scripts/lib/campaign-config.ts";
import { sellerResourceSchema } from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import { VoiceTraitsSchema } from "../../../skills/leadcue-reply/scripts/lib/voice.ts";

const source = { quote: z.string().min(1).max(4000) };
const ITEMS = [
  z.strictObject({
    kind: z.literal("qualification"),
    value: z.string().trim().min(1).max(2000),
    ...source,
  }),
  z.strictObject({
    kind: z.literal("example"),
    value: z.string().trim().min(1).max(1200),
    ...source,
  }),
  z.strictObject({
    kind: z.literal("answer"),
    value: z.strictObject({
      askedAbout: z.string().trim().min(1).max(500),
      answer: z.string().trim().min(1).max(2000),
    }),
    ...source,
  }),
  z.strictObject({
    kind: z.literal("offer"),
    value: z.string().trim().min(1).max(3000),
    ...source,
  }),
  z.strictObject({ kind: z.literal("fact"), value: z.string().trim().min(1).max(2000), ...source }),
  z.strictObject({
    kind: z.literal("duration"),
    value: z.number().int().min(1).max(480),
    ...source,
  }),
  z.strictObject({ kind: z.literal("resource"), value: sellerResourceSchema, ...source }),
  z.strictObject({ kind: z.literal("voice"), value: VoiceTraitsSchema, ...source }),
  z.strictObject({
    kind: z.literal("routing"),
    value: z.strictObject({
      owner: z.enum(["agency", "client"]),
      permittedActions: z.array(campaignActionSchema).min(1),
      handoff: z
        .strictObject({ name: z.string().trim().min(1).max(200), email: z.email() })
        .nullable(),
    }),
    ...source,
  }),
] as const;
/**
 * A booking item as stored. A proposal saved before `manual` was retired may still say "arrange
 * manually", so a read parses it; applying it is refused (`WritableBriefProposalSchema`).
 */
const StoredBookingItem = z.strictObject({
  kind: z.literal("booking"),
  value: z
    .strictObject({
      mode: z.enum(["manual", "booking_link_only"]),
      url: z.url({ protocol: /^https$/ }).nullable(),
    })
    .refine(
      (value) => value.mode !== "booking_link_only" || value.url !== null,
      "Supply the booking link",
    ),
  ...source,
});
/**
 * A booking item a new extraction may propose and a rep may apply: a booking link. The app keeps
 * no availability of its own (Dawid, 2026-09-30: "its either link or connector"), so a brief that
 * says times are arranged by hand proposes nothing here, and a connected calendar is connected in
 * settings rather than read from a document.
 */
const WritableBookingItem = z.strictObject({
  kind: z.literal("booking"),
  value: z.strictObject({
    mode: z.literal("booking_link_only"),
    url: z.url({ protocol: /^https$/ }),
  }),
  ...source,
});
const BriefItemSchema = z.discriminatedUnion("kind", [...ITEMS, StoredBookingItem]);
export type BriefItem = z.infer<typeof BriefItemSchema>;
const WritableBriefItemSchema = z.discriminatedUnion("kind", [...ITEMS, WritableBookingItem]);

function proposalOf<T extends z.ZodType<{ readonly kind: string; readonly quote: string }>>(
  item: T,
) {
  return z
    .strictObject({
      items: z.array(item).max(30),
      unsupported: z
        .array(z.strictObject({ ...source, reason: z.string().min(1).max(1000) }))
        .max(30),
    })
    .superRefine((proposal, ctx) => {
      if (
        new Set(proposal.items.map((item) => `${item.kind}:${item.quote}`)).size !==
        proposal.items.length
      )
        ctx.addIssue({
          code: "custom",
          message: "Each proposed value needs a distinct source span within its destination.",
        });
      for (const kind of ["offer", "duration", "voice", "booking", "routing"] as const)
        if (proposal.items.filter((item) => item.kind === kind).length > 1)
          ctx.addIssue({
            code: "custom",
            message: `Resolve conflicting ${kind} statements before proposing a value`,
          });
    });
}
/** A proposal as stored, which every read parses. */
export const BriefProposalSchema = proposalOf(BriefItemSchema);
export type BriefProposal = z.infer<typeof BriefProposalSchema>;
/** A proposal a new extraction may return and a rep may apply: no booking item says `manual`. */
export const WritableBriefProposalSchema = proposalOf(WritableBriefItemSchema);
export type WritableBriefProposal = z.infer<typeof WritableBriefProposalSchema>;
export const BriefReviewSchema = z.strictObject({
  id: z.uuid(),
  name: z.string(),
  source: z.string(),
  digest: z.string(),
  status: z.enum(["extracting", "review", "failed", "applied", "discarded"]),
  proposal: BriefProposalSchema.nullable(),
  current: z.array(z.strictObject({ id: z.string(), label: z.string(), value: z.string() })),
  failure: z.string().nullable(),
});
export type BriefReview = z.infer<typeof BriefReviewSchema>;
export const BriefDecisionSchema = z.discriminatedUnion("decision", [
  z.strictObject({ decision: z.literal("discard") }),
  z.strictObject({
    decision: z.literal("apply"),
    digest: z.string().length(64),
    proposal: WritableBriefProposalSchema,
  }),
]);
export type BriefDecision = z.infer<typeof BriefDecisionSchema>;

/** Broad documents require review instead of becoming one context-field answer. */
export const BRIEF_REVIEW_KEYS: ReadonlySet<string> = new Set([
  "seller_offer",
  "approved_facts",
  "qualification_context",
  "tone_preferences",
  "approved_examples",
]);
